<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Icon from 'next/icon/Icon.vue';
import Button from 'next/button/Button.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import { ATTACHMENT_TYPES } from '../constants';
import { downloadFile } from '@chatwoot/utils';

import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const props = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const hasError = ref(false);
const showGallery = ref(false);
const isDownloading = ref(false);

const { attachments } = useMessageContext();

const messageMediaAttachments = computed(() => {
  const mediaAttachments = attachments.value.filter(attachment =>
    [
      ATTACHMENT_TYPES.IMAGE,
      ATTACHMENT_TYPES.VIDEO,
      ATTACHMENT_TYPES.IG_REEL,
      ATTACHMENT_TYPES.AUDIO,
    ].includes(attachment.fileType)
  );

  return useSnakeCase(mediaAttachments);
});

const handleError = () => {
  hasError.value = true;
};

const downloadAttachment = async () => {
  const { fileType, dataUrl, extension } = props.attachment;

  try {
    isDownloading.value = true;
    await downloadFile({
      url: dataUrl,
      type: fileType,
      extension,
    });
  } catch (error) {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  } finally {
    isDownloading.value = false;
  }
};
</script>

<template>
  <div
    class="w-full max-w-[400px] min-h-[160px] overflow-hidden rounded-xl cursor-pointer"
    @click="showGallery = true"
  >
    <div
      v-if="hasError"
      class="flex flex-col items-center justify-center gap-1 text-xs text-center rounded-lg min-h-[160px] bg-n-alpha-1 text-n-slate-11"
    >
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      {{ $t('COMPONENTS.MEDIA.LOADING_FAILED') }}
    </div>

    <div v-else class="relative group w-full">
      <img
        class="block w-full h-auto object-contain rounded-xl skip-context-menu"
        :src="attachment.dataUrl"
        @error="handleError"
      />

      <div
        class="inset-0 p-2 pointer-events-none absolute bg-gradient-to-tl from-n-slate-12/30 dark:from-n-slate-1/50 via-transparent to-transparent hidden group-hover:flex"
      />

      <div class="absolute right-2 bottom-2 hidden group-hover:flex gap-2">
        <Button
          xs
          solid
          slate
          icon="i-lucide-expand"
          class="opacity-60"
          @click.stop="showGallery = true"
        />

        <Button
          xs
          solid
          slate
          icon="i-lucide-download"
          class="opacity-60"
          :is-loading="isDownloading"
          :disabled="isDownloading"
          @click.stop="downloadAttachment"
        />
      </div>
    </div>
  </div>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="messageMediaAttachments"
    @error="handleError"
    @close="() => (showGallery = false)"
  />
</template>
